import { format } from "date-fns";

interface FormFieldValue {
	fieldType: string;
	fieldValue: any;
}

interface FormFieldDef {
	id: string;
	type: string;
	label: string;
	options?: string[];
	odontogramFields?: FormFieldDef[];
	appointmentFields?: FormFieldDef[];
}

interface FormSectionDef {
	id: string;
	title: string;
	description?: string;
	fields: FormFieldDef[];
}

interface PdfFormFieldsProps {
	sections: FormSectionDef[];
	formData: Record<string, FormFieldValue>;
	formReferences: Record<string, string>;
	fileThumbnails?: Record<string, string>;
	excludedFieldIds?: string[];
}

const sectionTitleStyle: React.CSSProperties = {
	fontSize: "7pt",
	fontWeight: "bold",
	color: "#374151",
	marginTop: "2mm",
	marginBottom: "1mm",
};

const fieldRowStyle: React.CSSProperties = {
	fontSize: "7pt",
	lineHeight: "1.6",
	marginBottom: "1.2mm",
};

function formatFieldValue(
	field: FormFieldDef,
	data: FormFieldValue | undefined,
	references: Record<string, string>
): string {
	if (
		!data ||
		data.fieldValue === undefined ||
		data.fieldValue === null ||
		data.fieldValue === ""
	) {
		return "—";
	}

	const val = data.fieldValue;

	switch (field.type) {
		case "text":
		case "textarea":
			return String(val);
		case "select":
		case "radio": {
			const refKey = `${field.id}-${val}`;
			if (references[refKey]) return references[refKey];
			if (field.options && typeof val === "string") {
				const idx = parseInt(val, 10);
				if (!isNaN(idx) && field.options[idx])
					return field.options[idx];
			}
			return String(val);
		}
		case "checkbox": {
			if (Array.isArray(val)) {
				return val
					.map((v) => {
						const refKey = `${field.id}-${v}`;
						if (references[refKey]) return references[refKey];
						if (field.options) {
							const idx = parseInt(v, 10);
							if (!isNaN(idx) && field.options[idx])
								return field.options[idx];
						}
						return String(v);
					})
					.join(", ");
			}
			return String(val);
		}
		case "date": {
			try {
				return format(new Date(String(val)), "dd.MM.yyyy");
			} catch {
				return String(val);
			}
		}
		case "time":
			return String(val);
		case "file":
			return "";
		case "odontogram":
			return "";
		case "appointment":
			return "";
		default:
			return String(val);
	}
}

function renderFileField(
	data: FormFieldValue | undefined,
	fileThumbnails?: Record<string, string>
): React.ReactNode {
	if (!data?.fieldValue) return <div style={fieldRowStyle}>—</div>;
	const files = Array.isArray(data.fieldValue) ? data.fieldValue : [];
	if (files.length === 0) return <div style={fieldRowStyle}>—</div>;

	return (
		<div
			style={{
				display: "flex",
				flexWrap: "wrap",
				gap: "2mm",
				marginBottom: "2mm",
			}}
		>
			{files.map((file: any, idx: number) => {
				const name =
					typeof file === "string"
						? file
						: file?.name ?? `Datei ${idx + 1}`;
				const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(name);
				const fileId = typeof file === "object" ? file?.id : undefined;
				const thumbnailUrl = fileId && fileThumbnails?.[fileId];

				if (isImage && thumbnailUrl) {
					return (
						<div
							key={idx}
							style={{ width: "25mm", textAlign: "center" }}
						>
							<img
								src={thumbnailUrl}
								style={{
									width: "25mm",
									height: "20mm",
									objectFit: "cover",
									borderRadius: "1mm",
								}}
							/>
							<div
								style={{
									fontSize: "5pt",
									color: "#6b7280",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{name}
							</div>
						</div>
					);
				}
				return (
					<div
						key={idx}
						style={{
							fontSize: "7pt",
							color: "#374151",
							padding: "1mm 2mm",
							background: "#f3f4f6",
							borderRadius: "1mm",
						}}
					>
						{name}
					</div>
				);
			})}
		</div>
	);
}

function renderAppointmentField(
	field: FormFieldDef,
	data: FormFieldValue | undefined
): React.ReactNode {
	if (
		!data?.fieldValue ||
		!Array.isArray(data.fieldValue) ||
		data.fieldValue.length === 0
	) {
		return <div style={fieldRowStyle}>—</div>;
	}

	const appointments = data.fieldValue;
	const subFields = field.appointmentFields ?? [];

	return (
		<table
			style={{
				width: "100%",
				borderCollapse: "collapse",
				fontSize: "7pt",
				marginBottom: "2mm",
			}}
		>
			<thead>
				<tr>
					<th
						style={{
							textAlign: "left",
							padding: "1mm",
							borderBottom: "0.5pt solid #d1d5db",
							fontWeight: "bold",
							color: "#6b7280",
						}}
					>
						#
					</th>
					{subFields.map((sf) => (
						<th
							key={sf.id}
							style={{
								textAlign: "left",
								padding: "1mm",
								borderBottom: "0.5pt solid #d1d5db",
								fontWeight: "bold",
								color: "#6b7280",
							}}
						>
							{sf.label}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{appointments.map((appt: any, idx: number) => (
					<tr key={idx}>
						<td
							style={{
								padding: "1mm",
								borderBottom: "0.3pt solid #e5e7eb",
								color: "#374151",
							}}
						>
							{idx + 1}
						</td>
						{subFields.map((sf) => (
							<td
								key={sf.id}
								style={{
									padding: "1mm",
									borderBottom: "0.3pt solid #e5e7eb",
									color: "#374151",
								}}
							>
								{appt.values?.[sf.id] || "—"}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	);
}

export const PdfFormFields: React.FC<PdfFormFieldsProps> = ({
	sections,
	formData,
	formReferences,
	fileThumbnails,
	excludedFieldIds,
}) => {
	const skipTypes = new Set(["odontogram", "title", "description"]);
	const excludedIds = excludedFieldIds ? new Set(excludedFieldIds) : null;

	function isEmpty(data: FormFieldValue | undefined): boolean {
		if (
			!data ||
			data.fieldValue === undefined ||
			data.fieldValue === null ||
			data.fieldValue === ""
		)
			return true;
		if (Array.isArray(data.fieldValue) && data.fieldValue.length === 0)
			return true;
		return false;
	}

	return (
		<div>
			{sections.map((section) => {
				const visibleFields = section.fields.filter((f) => {
					if (skipTypes.has(f.type)) return false;
					if (excludedIds?.has(f.id)) return false;
					return !isEmpty(formData[f.id]);
				});

				if (visibleFields.length === 0) return null;

				return (
					<div key={section.id}>
						{section.title && (
							<div style={sectionTitleStyle}>{section.title}</div>
						)}
						{visibleFields.map((field) => {
							const data = formData[field.id];

							if (field.type === "file") {
								return (
									<div key={field.id} style={fieldRowStyle}>
										<span
											style={{
												fontWeight: "bold",
												color: "#6b7280",
											}}
										>
											{field.label}:{" "}
										</span>
										{renderFileField(data, fileThumbnails)}
									</div>
								);
							}

							if (field.type === "appointment") {
								return (
									<div key={field.id} style={fieldRowStyle}>
										<span
											style={{
												fontWeight: "bold",
												color: "#6b7280",
											}}
										>
											{field.label}:
										</span>
										{renderAppointmentField(field, data)}
									</div>
								);
							}

							const displayValue = formatFieldValue(
								field,
								data,
								formReferences
							);

							return (
								<div key={field.id} style={fieldRowStyle}>
									<span
										style={{
											fontWeight: "bold",
											color: "#6b7280",
										}}
									>
										{field.label}:{" "}
									</span>
									{field.type === "textarea" ? (
										<span
											style={{ whiteSpace: "pre-wrap" }}
										>
											{displayValue}
										</span>
									) : (
										<span>{displayValue}</span>
									)}
								</div>
							);
						})}
					</div>
				);
			})}
		</div>
	);
};
